import { useEffect, useRef } from 'react';

/**
 * Surah-complete celebration modal.
 *
 * Opens (alongside confetti) the first time a user crosses 100% on a
 * surah inside the Reader. Three actions:
 *   - Primary: Continue to next surah (or "Browse all surahs" for #114)
 *   - Secondary: Back to Reader (restart at Surah 1, Ayah 1)
 *   - Tertiary: Stay on this ayah (close modal)
 *
 * The trophy emblem at the top has an orchestrated reveal:
 *   1) 0–900ms entrance: halo bursts outward, trophy scales in with a
 *      bouncy overshoot and slight rotation wobble, six sparkle dots
 *      emit radially from the center at staggered times.
 *   2) 900ms onwards (ambient): trophy floats gently, halo pulses, and
 *      a diagonal shimmer sweeps across the icon every ~5 seconds.
 *   All animations are gated behind `motion-safe:` — users with
 *   prefers-reduced-motion see a static trophy.
 */
export function CompletionModal({
  open,
  surahName,
  surahNumber,
  totalAyahs,
  onContinue,
  onBackToReader,
  onClose,
}: {
  open: boolean;
  surahName: string | undefined;
  surahNumber: number;
  totalAyahs: number;
  /** Triggered by primary CTA. Receives the *next* surah number, or
   *  null if this was the last surah (the parent should send the user
   *  to the surah index in that case). */
  onContinue: (nextSurahNumber: number | null) => void;
  /** Secondary action — "Back to Reader" — restarts at Surah 1, Ayah 1.
   *  Implementation lives in the parent so we stay route-agnostic here. */
  onBackToReader: () => void;
  onClose: () => void;
}) {
  const primaryRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (open) primaryRef.current?.focus();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const isLastSurah = surahNumber >= 114;
  const nextSurahNumber = isLastSurah ? null : surahNumber + 1;

  // Six sparkle dots evenly distributed around the trophy. Each gets a
  // small angle offset and a staggered animation delay so they don't
  // all burst at once — feels more alive.
  const sparkleAngles = [0, 60, 120, 180, 240, 300];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="completion-modal-title"
      className="
        fixed inset-0 z-50 flex items-center justify-center
        bg-night-950/80 backdrop-blur-sm
        motion-safe:animate-in motion-safe:fade-in
      "
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Scoped keyframes for the trophy reveal. Kept inline so the
          rules live next to the markup that uses them — easier to
          tune, no global stylesheet pollution. */}
      <style>{TROPHY_KEYFRAMES}</style>

      <div
        className="
          relative mx-4 w-full max-w-md
          overflow-hidden rounded-3xl
          border border-beam/30 bg-night-800
          p-8 text-center
          shadow-[0_0_60px_-12px_rgba(0,229,255,0.35)]
        "
        onClick={(e) => e.stopPropagation()}
      >
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -top-12 left-1/2 h-32 w-32 -translate-x-1/2 rounded-full bg-beam/20 blur-3xl"
        />

        {/* Animated trophy emblem.
            Stack of overlapping layers (each absolutely positioned
            inside the 16x16 container):
              - Outer expanding ring (entrance burst)
              - Ambient pulsing halo (infinite)
              - Solid circle backdrop
              - Sparkle dots (6, staggered entrance)
              - The trophy SVG itself (bouncy entrance + float + shimmer) */}
        <div className="relative mx-auto mb-4 h-16 w-16">
          {/* Outer ring — bursts outward on entrance and dissipates */}
          <span
            aria-hidden="true"
            className="trophy-ring-burst absolute inset-0 rounded-full border-2 border-beam/50"
          />

          {/* Ambient halo — softly pulses forever */}
          <span
            aria-hidden="true"
            className="trophy-halo-pulse absolute inset-[-6px] rounded-full bg-beam/10"
          />

          {/* Solid circle backdrop with subtle border + glow */}
          <span
            aria-hidden="true"
            className="trophy-circle absolute inset-0 rounded-full border border-beam/40 bg-beam/10 shadow-[inset_0_0_18px_rgba(0,229,255,0.25)]"
          />

          {/* Sparkles — small dots that shoot outward from center */}
          {sparkleAngles.map((angle, i) => (
            <span
              key={angle}
              aria-hidden="true"
              className="trophy-sparkle pointer-events-none absolute left-1/2 top-1/2 h-1.5 w-1.5 -translate-x-1/2 -translate-y-1/2 rounded-full bg-beam"
              style={{
                // Each sparkle's angle is set via CSS custom prop so the
                // keyframe can compute its destination as
                // rotate(var(--angle)) translateY(-distance).
                ['--sparkle-angle' as string]: `${angle}deg`,
                animationDelay: `${0.35 + i * 0.05}s`,
              }}
            />
          ))}

          {/* The trophy itself — bouncy entrance + ambient float */}
          <span
            aria-hidden="true"
            className="
              trophy-icon relative flex h-full w-full items-center justify-center
              text-beam
              drop-shadow-[0_0_8px_rgba(0,229,255,0.55)]
            "
          >
            <TrophyIcon className="h-8 w-8" />
            {/* Diagonal shimmer — masked to the icon area */}
            <span
              aria-hidden="true"
              className="trophy-shimmer pointer-events-none absolute inset-0 overflow-hidden rounded-full"
            />
          </span>
        </div>

        <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-beam">
          Surah Complete
        </p>
        <h2
          id="completion-modal-title"
          className="font-display text-2xl font-extrabold text-night-50"
        >
          {surahName ?? 'Surah'}
        </h2>
        <p className="mt-2 text-sm text-night-200">
          You marked all {totalAyahs} ayahs as read.
        </p>

        <div className="mt-7 space-y-3">
          <button
            ref={primaryRef}
            type="button"
            onClick={() => onContinue(nextSurahNumber)}
            className="
              w-full rounded-lg
              bg-beam px-4 py-3 text-sm font-semibold uppercase tracking-wide text-beam-ink
              transition-colors hover:brightness-110
            "
          >
            {isLastSurah ? 'Browse all surahs →' : 'Continue to next surah →'}
          </button>
          {/* "Back to Reader" — restart from Surah 1, Ayah 1. Shown on
              every surah (including 114) so the user always has a
              one-click "start over" path inside the Reader experience. */}
          <button
            type="button"
            onClick={onBackToReader}
            className="
              w-full rounded-lg
              border border-night-500/40 bg-night-700/40 px-4 py-3
              text-sm font-semibold uppercase tracking-wide text-night-100
              transition-colors hover:bg-night-700/70
            "
          >
            Back to Reader
          </button>
          <button
            type="button"
            onClick={onClose}
            className="w-full py-2 text-xs font-medium text-night-300 transition-colors hover:text-night-100"
          >
            Stay on this ayah
          </button>
        </div>
      </div>
    </div>
  );
}

function TrophyIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M7 4h10v4a5 5 0 0 1-10 0V4Z" />
      <path d="M7 6H5a2 2 0 0 0 0 4h2" />
      <path d="M17 6h2a2 2 0 0 1 0 4h-2" />
      <path d="M12 13v3" />
      <path d="M9 19h6" />
      <path d="M10 16h4l-1 3h-2l-1-3Z" fill="currentColor" stroke="none" />
    </svg>
  );
}

/**
 * Keyframes for the trophy reveal. Wrapped in @media (prefers-reduced-motion: no-preference)
 * so users who've asked for less motion see a static trophy.
 *
 * Easing notes:
 *   - cubic-bezier(0.34, 1.56, 0.64, 1) is a popular "back-out" curve
 *     that overshoots slightly before settling — gives the bouncy
 *     "earned-it" feel without being cartoonish.
 *   - The float and pulse use ease-in-out so the motion feels organic.
 */
const TROPHY_KEYFRAMES = `
@media (prefers-reduced-motion: no-preference) {
  @keyframes trophy-entrance {
    0%   { opacity: 0; transform: scale(0.3) rotate(-15deg); }
    50%  { opacity: 1; transform: scale(1.25) rotate(8deg); }
    75%  {            transform: scale(0.92) rotate(-3deg); }
    100% { opacity: 1; transform: scale(1)    rotate(0deg);   }
  }

  @keyframes trophy-float {
    0%, 100% { transform: translateY(0); }
    50%      { transform: translateY(-2px); }
  }

  @keyframes trophy-shimmer {
    0%   { transform: translateX(-150%) skewX(-20deg); opacity: 0; }
    20%  {                                              opacity: 0.7; }
    50%  { transform: translateX(150%)  skewX(-20deg); opacity: 0; }
    100% { transform: translateX(150%)  skewX(-20deg); opacity: 0; }
  }

  @keyframes ring-burst {
    0%   { opacity: 0.9; transform: scale(0.6); }
    60%  { opacity: 0.45; }
    100% { opacity: 0;   transform: scale(2.0); }
  }

  @keyframes halo-pulse {
    0%, 100% { opacity: 0.30; transform: scale(1);    filter: blur(8px);  }
    50%      { opacity: 0.65; transform: scale(1.12); filter: blur(10px); }
  }

  @keyframes circle-pop {
    0%   { transform: scale(0);    opacity: 0; }
    65%  { transform: scale(1.10); opacity: 1; }
    100% { transform: scale(1);    opacity: 1; }
  }

  @keyframes sparkle-emit {
    0%   { opacity: 0; transform: translate(-50%, -50%) rotate(var(--sparkle-angle)) translateY(0)     scale(0); }
    25%  { opacity: 1; transform: translate(-50%, -50%) rotate(var(--sparkle-angle)) translateY(-14px) scale(1); }
    100% { opacity: 0; transform: translate(-50%, -50%) rotate(var(--sparkle-angle)) translateY(-34px) scale(0.2); }
  }

  /* The trophy icon: 700ms bouncy entrance, then forever-floating
     ambient motion. Composing two animations on the same element is
     fine since they target different properties (transform vs
     transform — but the second only runs after the first finishes). */
  .trophy-icon {
    animation:
      trophy-entrance 700ms cubic-bezier(0.34, 1.56, 0.64, 1) both,
      trophy-float 3s ease-in-out 900ms infinite;
  }

  /* The shimmer span uses a gradient as a pseudo-overlay. We can't put
     it on the SVG directly (the SVG fills currentColor), so we layer a
     transparent gradient over the same area. */
  .trophy-shimmer {
    background: linear-gradient(110deg,
      transparent 30%,
      rgba(255, 255, 255, 0.55) 50%,
      transparent 70%);
    animation: trophy-shimmer 5s ease-in-out 1200ms infinite;
  }

  .trophy-ring-burst {
    animation: ring-burst 900ms ease-out 100ms both;
  }

  .trophy-halo-pulse {
    animation: halo-pulse 2.5s ease-in-out 900ms infinite;
  }

  .trophy-circle {
    animation: circle-pop 500ms cubic-bezier(0.34, 1.56, 0.64, 1) both;
  }

  .trophy-sparkle {
    /* animationDelay is set inline per-sparkle (see the .map above). */
    animation: sparkle-emit 900ms ease-out both;
    box-shadow: 0 0 6px rgba(0, 229, 255, 0.9);
  }
}
`;
